import { supabase } from '@/integrations/supabase/client';

// Define required buckets here or import from a config file
const REQUIRED_BUCKETS = ['escort-photos', 'profile-images', 'content-items', 'verification-docs'];

/**
 * Test access to required Supabase storage buckets.
 */
export const testStorageBuckets = async () => {
  try {
    console.log('Health Check: Testing storage buckets...');
    
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();
    
    if (listError) {
      console.error('Health Check Error: Listing storage buckets:', listError);
      return { 
        success: false, 
        error: listError.message,
        message: 'Failed to list storage buckets',
        bucketsFound: [],
        accessResults: {}
      };
    }
    
    const foundBucketNames = buckets?.map(b => b.name) || [];
    console.log('Health Check: Storage buckets found:', foundBucketNames);
    
    const bucketAccessResults: Record<string, { success: boolean; exists: boolean; error?: string }> = {};
    let allRequiredAccessible = true;

    for (const bucketName of REQUIRED_BUCKETS) {
      const exists = foundBucketNames.includes(bucketName);
      if (!exists) {
          console.warn(`Health Check: Required bucket ${bucketName} does not exist.`);
          bucketAccessResults[bucketName] = { success: false, exists: false, error: 'Bucket does not exist' };
          allRequiredAccessible = false;
          continue; // Skip access test if bucket doesn't exist
      }
      
      try {
        // Test access by listing (limit 1 for efficiency)
        const { error: accessError } = await supabase.storage
          .from(bucketName)
          .list('', { limit: 1 }); 
        
        if (accessError) {
          console.error(`Health Check Error: Accessing bucket ${bucketName}:`, accessError);
          bucketAccessResults[bucketName] = { success: false, exists: true, error: accessError.message };
          allRequiredAccessible = false;
        } else {
          // console.log(`Health Check: Successfully accessed bucket ${bucketName}`);
          bucketAccessResults[bucketName] = { success: true, exists: true };
        }
      } catch (testError) {
        console.error(`Health Check Error: Testing bucket ${bucketName} failed unexpectedly:`, testError);
        bucketAccessResults[bucketName] = { 
            success: false, 
            exists: true, 
            error: testError instanceof Error ? testError.message : 'Unknown error during access test' 
        };
        allRequiredAccessible = false;
      }
    }
    
    const message = allRequiredAccessible 
      ? 'All required storage buckets exist and are accessible' 
      : 'Some required storage buckets are missing or inaccessible';
    console.log(`Health Check: Storage test result - ${message}`);

    return {
      success: allRequiredAccessible,
      message: message,
      bucketsFound: foundBucketNames,
      accessResults: bucketAccessResults
    };

  } catch (error) {
    console.error('Health Check Error: Unexpected error testing storage buckets:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute storage bucket test',
      bucketsFound: [],
      accessResults: {}
    };
  }
}; 